import React, { useState, useMemo } from 'react'
import './SourcesTable.css'

const endpoints = {
  'bot.dev.stratsys.net': 'https://data-ai-insights.svc.dev.stratsys.net',
  'bot.test.stratsys.net': 'https://data-ai-insights.svc.test.stratsys.net',
  'bot.stratsys.com': 'https://data-ai-insights.svc.stratsys.com',
  'localhost:8080': 'http://localhost:5001'
}

// ─────────────────────────────────────────────────────────────────────────────
// ChunkPreviewPopup – glass‑morphic popup for chunk content
// ─────────────────────────────────────────────────────────────────────────────
function ChunkPreviewPopup({ chunkData, isLoading, onClose }) {
  const stopProp = (e) => e.stopPropagation()

  return (
    <div className="chunk-popup-overlay" onClick={onClose}>
      <div className="chunk-popup" onClick={stopProp}>
        <button className="close-button chunk-close" onClick={onClose}>
          &times;
        </button>

        <h3 className="shining-text">Chunk Preview</h3>

        {isLoading ? (
          <div className="loading-message">Loading chunk content...</div>
        ) : chunkData && chunkData.length > 0 ? (
          chunkData.map((chunk, idx) => (
            <div className="chunk-content-node" key={idx}>
              <div className="chunk-header shining-text">
                {chunk.index}: {chunk.title}
              </div>
              <div className="chunk-content">{chunk.content}</div>
            </div>
          ))
        ) : (
          <div className="error-message">No content found for this chunk.</div>
        )}
      </div>
    </div>
  )
}

/**
 * SourcesTable component
 * @param {{ data: Record<string, Record<string, number>> }} props
 *  data – expects the shape { [docTitle]: { [chunkName]: usageCount } }
 */
const SourcesTable = ({ data = {} }) => {
  // Aggregate totals per document and sort by usage desc
  const docs = useMemo(() => {
    return Object.entries(data)
      .map(([docTitle, chunks]) => ({
        docTitle,
        total: Object.entries(chunks)
          .filter(([chunkName]) => chunkName !== 'indexes')
          .reduce((sum, [, count]) => sum + count, 0),
        chunks
      }))
      .sort((a, b) => b.total - a.total) // most‑used first
  }, [data])

  // Track which documents are expanded
  const [expanded, setExpanded] = useState(new Set())
  const toggleDoc = (title) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(title) ? next.delete(title) : next.add(title)
      return next
    })
  }

  // Chunk preview popup state
  const [chunkPopupOpen, setChunkPopupOpen] = useState(false)
  const [chunkData, setChunkData] = useState([])
  const [isLoadingChunk, setIsLoadingChunk] = useState(false)

  // Handle chunk click to fetch and show content
  const handleChunkClick = async (docTitle, chunkName) => {
    if (chunkName === 'indexes') return // Skip indexes entry

    setChunkPopupOpen(true)
    setIsLoadingChunk(true)
    setChunkData([])

    try {
      // Get indexes data for this document
      const indexes = data[docTitle]?.indexes || []

      const res = await window.PDR.api.post(`${endpoints[window.location.host]}/fetch_sources_content`, {
        sources: [chunkName],
        indexes: indexes
      })

      setChunkData(res)
    } catch (error) {
      console.error('Error fetching chunk content:', error)
      setChunkData([])
    } finally {
      setIsLoadingChunk(false)
    }
  }

  return (
    <div className="sources-table-container">
      <div className="table-wrapper-source">
        <table className="sources-table">
          <thead>
            <tr>
              <th className="document-col">Document</th>
              <th className="count-col">Uses</th>
            </tr>
          </thead>
          <tbody>
            {docs.map(({ docTitle, total, chunks }) => {
              const isOpen = expanded.has(docTitle)
              return (
                <React.Fragment key={docTitle}>
                  {/* Parent document row */}
                  <tr
                    className={`doc-row ${isOpen ? 'expanded' : ''}`}
                    onClick={() => toggleDoc(docTitle)}
                    title="Click to expand"
                    style={{ backgroundColor: isOpen ? '#e8f4ff' : undefined }}>
                    <td>
                      <span className="toggle-icon" style={{ marginRight: 6 }}>
                        {isOpen ? '▼' : '▶'}
                      </span>
                      {docTitle}
                    </td>
                    <td>{total}</td>
                  </tr>

                  {/* Chunk rows (rendered when expanded) */}
                  {isOpen &&
                    Object.entries(chunks).map(([chunkName, count], idx) => (
                      <tr
                        key={`${docTitle}-${chunkName}`}
                        className={`chunk-row ${idx % 2 === 0 ? 'even' : 'odd'}`}
                        onClick={() => handleChunkClick(docTitle, chunkName)}
                        title="Click to preview chunk">
                        <td className="chunk-name">{chunkName}</td>
                        <td className="chunk-count">{count}</td>
                      </tr>
                    ))}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Chunk Preview Popup */}
      {chunkPopupOpen && (
        <ChunkPreviewPopup chunkData={chunkData} isLoading={isLoadingChunk} onClose={() => setChunkPopupOpen(false)} />
      )}
    </div>
  )
}

export default SourcesTable
